/**
 * D-22 (DN-1, DN-2, DN-7) — the vault's per-device, NON-SECRET settings and the server's
 * `GET /settings` answer (contract 3.3.0).
 *
 *  - DN-1 "Lock after": each device picks how long an unlock lasts without activity. The vault
 *    caps what it accepts (never above 72 h, lower when policy says so) and lists the allowed
 *    durations in `GET /settings`; a device only offers those. Sent on every unlock.
 *  - DN-7 "Clear clipboard": each device picks when a copied secret is cleared, incl. never.
 *  - DN-2 "Card fill approval": a per-USER server preference (`POST /settings/preferences`),
 *    which can only make the admin policy stricter. Not stored on the device.
 *
 * D-24 (contract 3.8.0, settings-first):
 *  - "Auto-hide shown secrets": a per-USER server preference (30 s / 2 min default / 5 min / until I
 *    hide it), capped by policy `reveal_autohide_max_seconds`. Not stored on the device.
 *  - "Require master password after restart": a per-DEVICE switch (default off) stored here; admin
 *    policy may enforce it on. See client-run.ts for what a restart is on each surface.
 *  - `setByAdmin`: which settings an admin policy bounds ("Set by your admin").
 *
 * Device settings hold choices, never a token, secret, username or URL. Hosts keep them in
 * their own non-secret local storage (Hub web: localStorage per browser; extension:
 * chrome.storage.local; desktop: its window's localStorage; iOS: UserDefaults).
 * Pure module: no DOM, no storage, unit-testable.
 */
import { z } from "zod";

// ---- DN-1: unlock duration ("Lock after") -------------------------------------------------

/** Mirrors wtc-vault `UNLOCK_DURATIONS` (contract 3.3.0), shortest first. */
export const UNLOCK_DURATIONS = ["5m", "15m", "1h", "6h", "12h", "24h", "72h"] as const;
export const UnlockDuration = z.enum(UNLOCK_DURATIONS);
export type UnlockDuration = z.infer<typeof UnlockDuration>;

export const UNLOCK_DURATION_MINUTES: Readonly<Record<UnlockDuration, number>> = {
  "5m": 5,
  "15m": 15,
  "1h": 60,
  "6h": 360,
  "12h": 720,
  "24h": 1440,
  "72h": 4320,
};

export const UNLOCK_DURATION_LABELS: Readonly<Record<UnlockDuration, string>> = {
  "5m": "5 minutes",
  "15m": "15 minutes",
  "1h": "1 hour",
  "6h": "6 hours",
  "12h": "12 hours",
  "24h": "24 hours",
  "72h": "3 days",
};

/** Short form for chips and footers ("1 h", "3 days"). */
export const UNLOCK_DURATION_SHORT: Readonly<Record<UnlockDuration, string>> = {
  "5m": "5 min",
  "15m": "15 min",
  "1h": "1 h",
  "6h": "6 h",
  "12h": "12 h",
  "24h": "24 h",
  "72h": "3 days",
};

/** A new device's default (D-22 DN-1). */
export const DEFAULT_UNLOCK_DURATION: UnlockDuration = "1h";

/**
 * The duration a device should send: its own choice when the vault allows it, else the longest
 * allowed duration that is not longer than the choice, else the server's default (or the
 * shortest allowed). `allowed` empty (no `/settings` answer yet) = send the choice as is; the
 * vault caps it anyway.
 */
export function effectiveUnlockDuration(
  choice: UnlockDuration,
  allowed: readonly UnlockDuration[],
  serverDefault?: UnlockDuration | null,
): UnlockDuration {
  if (allowed.length === 0) return choice;
  if (allowed.includes(choice)) return choice;
  const limit = UNLOCK_DURATION_MINUTES[choice];
  const shorter = UNLOCK_DURATIONS.filter((d) => allowed.includes(d) && UNLOCK_DURATION_MINUTES[d] <= limit);
  const best = shorter[shorter.length - 1];
  if (best) return best;
  if (serverDefault && allowed.includes(serverDefault)) return serverDefault;
  const first = UNLOCK_DURATIONS.find((d) => allowed.includes(d));
  return first ?? choice;
}

/**
 * wtc-vault #47: without a device key (no `x-vault-proof`) a session is capped at this many
 * minutes whatever was picked (`GET /settings` → `unlock.unboundMaxHours`, 12 h).
 */
export const MAX_UNBOUND_MINUTES = 12 * 60;

/**
 * The durations to OFFER on this device: the vault's allowed list (or all, when unknown), and
 * the longer ones (24 h, 3 days) only when this device's unlock is device-key bound (#47).
 */
export function durationsForDevice(allowed: readonly UnlockDuration[], deviceBound: boolean, unboundMaxMinutes: number = MAX_UNBOUND_MINUTES): UnlockDuration[] {
  const base: readonly UnlockDuration[] = allowed.length > 0 ? allowed : UNLOCK_DURATIONS;
  return deviceBound ? [...base] : base.filter((d) => UNLOCK_DURATION_MINUTES[d] <= unboundMaxMinutes);
}

/** The closest named duration for a number of minutes (for a session adopted from elsewhere). */
export function unlockDurationForMinutes(minutes: number): UnlockDuration {
  let best: UnlockDuration = UNLOCK_DURATIONS[0];
  for (const d of UNLOCK_DURATIONS) if (UNLOCK_DURATION_MINUTES[d] <= minutes) best = d;
  return best;
}

/** "1 hour", or "1 h 30 min" style for a non-standard idle limit the vault answered. */
export function describeMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const named = UNLOCK_DURATIONS.find((d) => UNLOCK_DURATION_MINUTES[d] === m);
  if (named) return UNLOCK_DURATION_LABELS[named];
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} h` : `${h} h ${rest} min`;
}

// ---- DN-7: clipboard clear ----------------------------------------------------------------

export const CLIPBOARD_CLEAR_CHOICES = ["30s", "1m", "5m", "15m", "never"] as const;
export const ClipboardClear = z.enum(CLIPBOARD_CLEAR_CHOICES);
export type ClipboardClear = z.infer<typeof ClipboardClear>;

/** Seconds until the clear, or null for "never". */
export const CLIPBOARD_CLEAR_SECONDS: Readonly<Record<ClipboardClear, number | null>> = {
  "30s": 30,
  "1m": 60,
  "5m": 300,
  "15m": 900,
  never: null,
};

export const CLIPBOARD_CLEAR_LABELS: Readonly<Record<ClipboardClear, string>> = {
  "30s": "After 30 seconds",
  "1m": "After 1 minute",
  "5m": "After 5 minutes",
  "15m": "After 15 minutes",
  never: "Never",
};

const CLIPBOARD_CLEAR_SHORT: Readonly<Record<Exclude<ClipboardClear, "never">, string>> = {
  "30s": "30 s",
  "1m": "1 min",
  "5m": "5 min",
  "15m": "15 min",
};

/** A new device's default (D-22 DN-7). */
export const DEFAULT_CLIPBOARD_CLEAR: ClipboardClear = "1m";

export function clipboardClearMs(choice: ClipboardClear): number | null {
  const s = CLIPBOARD_CLEAR_SECONDS[choice];
  return s === null ? null : s * 1000;
}

/** Toast copy: "Copied password — clears in 1 min", or just "Copied password" for never. */
export function copiedToastText(label: string, choice: ClipboardClear): string {
  const what = label.trim().length > 0 ? `Copied ${label.trim().toLowerCase()}` : "Copied";
  return choice === "never" ? what : `${what} — clears in ${CLIPBOARD_CLEAR_SHORT[choice]}`;
}

/** Live-countdown variant ("Copied password · clears in 42 s"), for the countdown toast. */
export function copiedCountdownText(label: string, secondsLeft: number): string {
  const what = label.trim().length > 0 ? `Copied ${label.trim().toLowerCase()}` : "Copied";
  const s = Math.max(0, Math.ceil(secondsLeft));
  return s >= 120 ? `${what} · clears in ${Math.ceil(s / 60)} min` : `${what} · clears in ${s} s`;
}

// ---- DN-2: card fill approval (per user, server-side) ---------------------------------------

export const CARD_FILL_APPROVAL_MODES = ["once_per_unlock", "every_fill"] as const;
export const CardFillApprovalMode = z.enum(CARD_FILL_APPROVAL_MODES);
export type CardFillApprovalMode = z.infer<typeof CardFillApprovalMode>;

export const CARD_FILL_APPROVAL_LABELS: Readonly<Record<CardFillApprovalMode, string>> = {
  once_per_unlock: "Once per unlock",
  every_fill: "Every time a card is filled",
};

/** True when the user may pick `mode` under `policy` (a preference can only be stricter). */
export function cardFillModeAllowed(mode: CardFillApprovalMode, policy: CardFillApprovalMode): boolean {
  return policy === "once_per_unlock" || mode === "every_fill";
}

// ---- D-24: auto-hide of shown secrets (per user, server-side) ------------------------------

export const REVEAL_AUTOHIDE_CHOICES = ["30s", "2m", "5m", "manual"] as const;
export const RevealAutoHide = z.enum(REVEAL_AUTOHIDE_CHOICES);
export type RevealAutoHide = z.infer<typeof RevealAutoHide>;

/** Seconds on screen; null = until the user hides it. Mirrors wtc-vault `REVEAL_AUTOHIDE_SECONDS`. */
export const REVEAL_AUTOHIDE_SECONDS: Readonly<Record<RevealAutoHide, number | null>> = { "30s": 30, "2m": 120, "5m": 300, manual: null };

export const REVEAL_AUTOHIDE_LABELS: Readonly<Record<RevealAutoHide, string>> = {
  "30s": "After 30 seconds",
  "2m": "After 2 minutes",
  "5m": "After 5 minutes",
  manual: "Until I hide it",
};

/** D-24 default (as built in D-22): 2 minutes. */
export const DEFAULT_REVEAL_AUTOHIDE: RevealAutoHide = "2m";
export const DEFAULT_REVEAL_SECONDS = 120;

/** Within an admin cap (seconds; 0 = no cap)? "Until I hide it" is the loosest, so any cap rules it out. */
export function revealAutoHideAllowed(choice: RevealAutoHide, capSeconds: number): boolean {
  if (capSeconds <= 0) return true;
  const sec = REVEAL_AUTOHIDE_SECONDS[choice];
  return sec !== null && sec <= capSeconds;
}

/**
 * How long a shown secret stays on screen here: the vault's effective answer (3.8.0), else the
 * 2-minute default (older vaults, or no `/settings` answer yet). null = until the user hides it.
 */
export function revealVisibleSeconds(settings: VaultSettings | null | undefined): number | null {
  const r = settings?.revealAutoHide;
  if (!r) return DEFAULT_REVEAL_SECONDS;
  return r.effectiveSeconds;
}

// ---- D-24: "Require master password after restart" (per device) ---------------------------------

export interface RestartRuleState {
  /** The switch shows on. */
  readonly on: boolean;
  /** The user cannot change it (policy enforces it, or master-password unlock is not allowed). */
  readonly locked: boolean;
  /** Master-password unlock is allowed for this user at all. */
  readonly available: boolean;
  /** One line under the switch. */
  readonly note: string | null;
}

/**
 * The switch's state from this device's choice and the vault's answer. Policy can force it on;
 * nobody can turn it on without master-password unlock being allowed. Older vaults (no field):
 * the device choice is shown but the vault does not enforce it.
 */
export function restartRuleState(deviceChoice: boolean, settings: VaultSettings | null | undefined): RestartRuleState {
  const r = settings?.masterPasswordAfterRestart;
  if (!r) return { on: deviceChoice, locked: false, available: true, note: settings ? "Your vault doesn't support this setting yet." : null };
  if (!r.available) return { on: false, locked: true, available: false, note: "Master password unlock is turned off by your admin." };
  const on = r.policyRequires || deviceChoice;
  let note: string | null = null;
  if (r.policyRequires) note = "Set by your admin.";
  if (on && !r.masterPasswordSet) note = `${note ? `${note} ` : ""}Set a master password to use this; until then this device unlocks as usual.`;
  return { on, locked: r.policyRequires, available: true, note };
}

// ---- DN-4: login match mode ---------------------------------------------------------------

export const MATCH_MODES = ["domain", "exact"] as const;
export const MatchMode = z.enum(MATCH_MODES);
export type MatchMode = z.infer<typeof MatchMode>;

export const MATCH_MODE_LABELS: Readonly<Record<MatchMode, string>> = {
  domain: "Whole domain",
  exact: "Exact address only",
};

// ---- the device record ----------------------------------------------------------------------

/** Every field falls back on its own, so a damaged or older record never blocks the vault. */
export const DeviceSettings = z.object({
  unlockDuration: UnlockDuration.catch(DEFAULT_UNLOCK_DURATION),
  clipboardClear: ClipboardClear.catch(DEFAULT_CLIPBOARD_CLEAR),
  /** D-24: ask for the master password (not the device prompt) on the first unlock after a restart. */
  requireMasterPasswordAfterRestart: z.boolean().catch(false),
  /**
   * When the user last changed a setting (ms since epoch; 0 = never). Hub web and the extension
   * in the same browser are one device: they exchange settings and the newer record wins.
   */
  updatedAt: z.number().int().nonnegative().catch(0),
});
export type DeviceSettings = z.infer<typeof DeviceSettings>;

export const DEFAULT_DEVICE_SETTINGS: DeviceSettings = {
  unlockDuration: DEFAULT_UNLOCK_DURATION,
  clipboardClear: DEFAULT_CLIPBOARD_CLEAR,
  requireMasterPasswordAfterRestart: false,
  updatedAt: 0,
};

/** The newer of two records (ties keep `local`). */
export function newerDeviceSettings(local: DeviceSettings, other: DeviceSettings | null | undefined): DeviceSettings {
  return other && other.updatedAt > local.updatedAt ? other : local;
}

/** The storage key every web host uses (localStorage / chrome.storage.local). */
export const DEVICE_SETTINGS_KEY = "wtc_vault_device_settings";

/** Parse whatever a store returned (string JSON, object or nothing) into settings. */
export function parseDeviceSettings(raw: unknown): DeviceSettings {
  let value: unknown = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      value = null;
    }
  }
  if (typeof value !== "object" || value === null) return { ...DEFAULT_DEVICE_SETTINGS };
  const parsed = DeviceSettings.safeParse(value);
  return parsed.success ? parsed.data : { ...DEFAULT_DEVICE_SETTINGS };
}

// ---- GET /settings (contract 3.3.0) ---------------------------------------------------------

export const VaultSettings = z.object({
  unlock: z.object({
    /** Only the durations the effective policy allows, shortest first. */
    options: z.array(z.object({ duration: UnlockDuration, minutes: z.number().int().positive() })),
    /** What a new device should preselect (1 h, or the longest allowed if 1 h is not). */
    defaultDuration: UnlockDuration,
    maxIdleMinutes: z.number().int().positive(),
    maxSessionHours: z.number().int().positive(),
    absoluteFloorHours: z.number().int().positive(),
    /** #47: the cap for a session without a device key (hours). Older 3.3.0 builds omit it = 12. */
    unboundMaxHours: z.number().int().positive().default(12),
  }),
  cardFillApproval: z.object({
    effective: CardFillApprovalMode,
    policy: CardFillApprovalMode,
    preference: CardFillApprovalMode.nullable(),
    approvedThisUnlock: z.boolean(),
  }),
  autofill: z.object({ types: z.array(z.string()), defaultMatchMode: MatchMode }),
  // ---- 3.8.0 (D-24). Optional: a 3.7.0 vault omits them and the clients use the D-22 defaults.
  revealAutoHide: z
    .object({
      effective: RevealAutoHide.nullable(),
      effectiveSeconds: z.number().int().positive().nullable(),
      preference: RevealAutoHide.nullable(),
      policyMaxSeconds: z.number().int().nonnegative(),
      options: z.array(z.object({ choice: RevealAutoHide, seconds: z.number().int().positive().nullable(), allowed: z.boolean() })),
    })
    .optional(),
  masterPasswordAfterRestart: z.object({ policyRequires: z.boolean(), available: z.boolean(), masterPasswordSet: z.boolean() }).optional(),
  sharing: z
    .object({ itemSharing: z.boolean(), personalItemSharing: z.boolean(), outsideGroup: z.boolean(), itemSharesOnTransfer: z.enum(["drop", "keep"]) })
    .optional(),
  setByAdmin: z.record(z.string(), z.boolean()).optional(),
});
export type VaultSettings = z.infer<typeof VaultSettings>;

/** D-24: the settings an admin policy may bound (keys of `VaultSettings.setByAdmin`). */
export const SETTING_NAMES = [
  "unlockDuration", "cardFillApproval", "autofillTypes", "autosaveTypes", "revealAutoHide", "masterPasswordAfterRestart",
  "masterPassword", "deviceUnlock", "syncedPasskeys", "itemSharing", "generator",
] as const;
export type SettingName = (typeof SETTING_NAMES)[number];

/** D-24: does an admin policy lock or bound this setting? ("Set by your admin"). False on older vaults. */
export function isSetByAdmin(settings: VaultSettings | null | undefined, name: SettingName): boolean {
  return settings?.setByAdmin?.[name] === true;
}

/** D-24: the copy every surface shows next to a bounded control. */
export const SET_BY_ADMIN = "Set by your admin";

/** `POST /settings/preferences` (3.8.0): each key optional; null = back to the default. */
export interface PreferencesInput {
  readonly cardFillApproval?: CardFillApprovalMode | null;
  readonly revealAutoHide?: RevealAutoHide | null;
}

/** `POST /item-shares/revoke-all` (D-24 admin). */
export const RevokeAllItemSharesResult = z.object({ revoked: z.number().int().nonnegative(), items: z.number().int().nonnegative(), remaining: z.number().int().nonnegative() });
export type RevokeAllItemSharesResult = z.infer<typeof RevokeAllItemSharesResult>;
/** What the admin must type. Mirrors wtc-vault `REVOKE_ALL_CONFIRM`. */
export const REVOKE_ALL_CONFIRM = "REVOKE ALL ITEM SHARES";

/** The allowed durations from a `/settings` answer (empty when there is none yet). */
export function allowedUnlockDurations(settings: VaultSettings | null | undefined): UnlockDuration[] {
  return settings ? settings.unlock.options.map((o) => o.duration) : [];
}
